import { useEffect, useMemo, useState } from "react";
import { imageSizeTemplates } from "../adAssistantDimensions";

const VERTICAL_ASPECT_RATIO = {
  aspectRatio: 0.5625, // 9:16
  cloudAr: "9:16"
};

const PORTRAIT_ASPECT_RATIO = {
  aspectRatio: 1, // 1:1
  cloudAr: "1:1"
};

const LANDSCAPE_ASPECT_RATIO = {
  aspectRatio: 1.91, // 16:9
  cloudAr: "16:9"
};

// Predefined sizes for different breakpoints

const SIZES = {
  default: {
    PortraitImage: { width: 241, height: 241 },
    LandscapeImage: { width: 230, height: 120 },
    VerticalImage: { width: 225, height: 400 }
  },
  1440: {
    PortraitImage: { width: 290, height: 290 },
    LandscapeImage: { width: 310, height: 162 },
    VerticalImage: { width: 280, height: 498 }
  },
  1920: {
    PortraitImage: { width: 320, height: 320 },
    LandscapeImage: { width: 360, height: 189 },
    VerticalImage: { width: 288, height: 512 }
  },
  2560: {
    PortraitImage: { width: 380, height: 380 },
    LandscapeImage: { width: 420, height: 220 },
    VerticalImage: { width: 360, height: 640 }
  }
};

export const useAdAssistantDimensions = () => {
  const [screenData, setScreenData] = useState({
    screenWidth: window.innerWidth
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenData({
        screenWidth: window.innerWidth
      });
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  return useMemo(() => {
    const { screenWidth } = screenData;

    let sizes;

    // Apply sizes based on screen width
    if (screenWidth >= 2560) {
      sizes = SIZES[2560];
    } else if (screenWidth >= 1920) {
      sizes = SIZES[1920];
    } else if (screenWidth >= 1440) {
      sizes = SIZES[1440];
    } else {
      sizes = SIZES.default;
    }

    // Map sizes and append aspect ratios
    return {
      PortraitImage: { ...sizes.PortraitImage, ...PORTRAIT_ASPECT_RATIO },
      LandscapeImage: { ...sizes.LandscapeImage, ...LANDSCAPE_ASPECT_RATIO },
      VerticalImage: { ...sizes.VerticalImage, ...VERTICAL_ASPECT_RATIO },
      WaveImage: {
        vertical: {
          ...sizes.VerticalImage,
          ...VERTICAL_ASPECT_RATIO,
          ...imageSizeTemplates.WaveImage.vertical
        },
        portrait: {
          ...sizes.PortraitImage,
          ...PORTRAIT_ASPECT_RATIO,
          ...imageSizeTemplates.WaveImage.portrait
        },
        landscape: {
          ...sizes.LandscapeImage,
          ...LANDSCAPE_ASPECT_RATIO,
          ...imageSizeTemplates.WaveImage.landscape
        }
      },
      OneToneImage: {
        vertical: {
          ...sizes.VerticalImage,
          ...VERTICAL_ASPECT_RATIO,
          ...imageSizeTemplates.OneToneImage.vertical
        },
        portrait: {
          ...sizes.PortraitImage,
          ...PORTRAIT_ASPECT_RATIO,
          ...imageSizeTemplates.OneToneImage.portrait
        },
        landscape: {
          ...sizes.LandscapeImage,
          ...LANDSCAPE_ASPECT_RATIO,
          ...imageSizeTemplates.OneToneImage.landscape
        }
      },
      OneToneVideo: {
        vertical: { ...sizes.VerticalImage, ...VERTICAL_ASPECT_RATIO },
        portrait: { ...sizes.PortraitImage, ...PORTRAIT_ASPECT_RATIO },
        landscape: { ...sizes.LandscapeImage, ...LANDSCAPE_ASPECT_RATIO }
      },
      WaveVideo: {
        vertical: { ...sizes.VerticalImage, ...VERTICAL_ASPECT_RATIO },
        portrait: { ...sizes.PortraitImage, ...PORTRAIT_ASPECT_RATIO },
        landscape: { ...sizes.LandscapeImage, ...LANDSCAPE_ASPECT_RATIO }
      },
      DefaultImage: {
        vertical: {
          ...sizes.VerticalImage,
          ...VERTICAL_ASPECT_RATIO,
          ...imageSizeTemplates.DefaultImage.vertical
        },
        portrait: {
          ...sizes.PortraitImage,
          ...PORTRAIT_ASPECT_RATIO,
          ...imageSizeTemplates.DefaultImage.portrait
        },
        landscape: {
          ...sizes.LandscapeImage,
          ...LANDSCAPE_ASPECT_RATIO,
          ...imageSizeTemplates.DefaultImage.landscape
        }
      },
      DefaultVideo: {
        vertical: { ...sizes.VerticalImage, ...VERTICAL_ASPECT_RATIO },
        portrait: { ...sizes.PortraitImage, ...PORTRAIT_ASPECT_RATIO },
        landscape: { ...sizes.LandscapeImage, ...LANDSCAPE_ASPECT_RATIO }
      },
      default: { ...sizes.PortraitImage, ...PORTRAIT_ASPECT_RATIO }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenData.screenWidth, screenData.isRetina]);
};
